import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'
import { useQuery, useMutation } from '@apollo/client'
import { Field, Form, Formik } from 'formik'

import { withApolloV4Provider } from 'utilities/apollo'
import { media } from 'utilities/styled'
import toast from 'utilities/toast'

import {
  FormikSelectInput,
  FormikTextInput,
} from 'components/molecules/formik-field'
import Typography from 'components/molecules/typography'
import LoadingButton from 'components/atoms/loading-button'
import LoadingIndicator from 'components/atoms/loading-indicator'
import CategorizedDropdown from 'components/molecules/categorized-dropdown'
import FlexibleDialog from 'components/molecules/flexible-dialog'

import {
  GET_GPSR_CATEGORY_LIST,
  GET_GPSR_BRANDS_BY_CATEGORY,
  GET_GPSR_PARTIES_BY_BRAND,
  UPDATE_GPSR_PARTY_BY_BRAND,
  INSERT_BRAND,
} from 'config/graphql/v4'

import { GpsrFormSchema } from 'config/validation-schemas'
import NewTextInput from 'components/atoms/new-text-input'
import { GPSR_BRANDS_SOURCES, UNCATEGORIZED_GROUP_KEY } from 'config/consts'
import {
  serializeBrandIdentifier,
  deserializeBrandIdentifier,
  createBrandIdentifier,
} from 'utilities/gpsr'
import { GpsrTypeEnum } from 'config/enums'

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.sizings.lvl2} ${({ theme }) => theme.sizings.lvl3};
  max-width: ${({ theme }) => theme.sizeByFactor(80)};

  ${media.tablet`
    grid-template-columns: 1fr 1fr;
  `}
`

const StyledDropdown = styled(FormikSelectInput)`
  background-color: ${({ theme }) =>
    theme.colors.inputBackground || theme.colors.fieldBackground};
`

const StyledTextInput = styled(FormikTextInput)`
  background-color: ${({ theme }) =>
    theme.colors.inputBackground || theme.colors.fieldBackground};
`

const StyledForm = styled(Form)`
  width: 100%;
`

const SubmitButton = styled(LoadingButton)`
  margin-top: ${({ theme }) => theme.sizings.lvl4};
`

const FormSection = styled.div`
  margin-top: ${({ theme }) => theme.sizings.lvl3};
`

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizings.lvl2};
`

const EMPTY_FORM_VALUES = {
  categoryId: null,
  category: '',
  brand: serializeBrandIdentifier(null),
  brandId: null,
  brandLabel: '',
  source: '',
  manufacturerName: '',
  address: '',
  place: '',
  province: '',
  postalCode: '',
  phoneNumber: '',
  website: '',
  email: '',
  type: '',
}

const TYPE_OPTIONS = (t) => [
  { label: t('gpsrForm.manufacturer'), value: GpsrTypeEnum.MANUFACTURER },
  { label: t('gpsrForm.representative'), value: GpsrTypeEnum.REPRESENTATIVE },
]

const LocationsGpsr = () => {
  const { t } = useTranslation()
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const [selectedBrand, setSelectedBrand] = useState(null)
  const [isAddBrandDialogOpen, setIsAddBrandDialogOpen] = useState(false)
  const [newBrandName, setNewBrandName] = useState('')
  // Flag to show loading state during refetch operations
  const [refetchingDataAfterSubmit, setRefetchingDataAfterSubmit] =
    useState(false)

  // Queries
  const {
    data: categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useQuery(GET_GPSR_CATEGORY_LIST)

  const {
    data: brands,
    loading: brandsLoading,
    refetch: refetchBrands,
  } = useQuery(GET_GPSR_BRANDS_BY_CATEGORY, {
    variables: { category_id: selectedCategoryId },
    skip: !selectedCategoryId,
    fetchPolicy: 'no-cache',
  })

  const {
    data: partyData,
    loading: partyLoading,
    error: partyError,
    refetch: refetchParty,
  } = useQuery(GET_GPSR_PARTIES_BY_BRAND, {
    variables: {
      brand_id: selectedBrand?.id || null,
      source: selectedBrand?.source || null,
    },
    skip: !selectedBrand?.id || !selectedBrand?.source,
    fetchPolicy: 'network-only',
  })

  const [
    updateParty,
    { loading: updatePartyLoading, error: updatePartyError },
  ] = useMutation(UPDATE_GPSR_PARTY_BY_BRAND, {
    awaitRefetchQueries: true,
  })

  const [
    insertBrand,
    { loading: insertBrandLoading, error: insertBrandError },
  ] = useMutation(INSERT_BRAND)

  // Show loading indicator for critical loading states and during refetch operations
  const isInitialLoading =
    categoriesLoading || updatePartyLoading || insertBrandLoading
  const isError = categoriesError || updatePartyError || insertBrandError

  // Show loading when specifically refetching after form submission
  const isFormLoading = isInitialLoading || refetchingDataAfterSubmit

  const getInitialFormValues = () => {
    const selectedCategory = categories?.gpsrCategoryList?.find(
      (c) => c.id === selectedCategoryId,
    )

    /*
     * Find the correct brand data for the selected brand
     * We need to do this because the brand can exist in admin and tenant databases with the same id
     * and we need to be able to distinguish between them
     */
    const brandData = selectedBrand
      ? brands?.gpsrBrandsByCategory?.find(
          (b) => b.id === selectedBrand.id && b.source === selectedBrand.source,
        ) || null
      : null

    return {
      ...EMPTY_FORM_VALUES,
      categoryId: selectedCategoryId,
      category: selectedCategory?.category || '',
      brand: serializeBrandIdentifier(selectedBrand),
      brandId: selectedBrand?.id || null,
      brandLabel: brandData?.brand || '',
      source: selectedBrand?.source || '',
    }
  }

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      await updateParty({
        variables: {
          brand_id: values.brandId,
          source: values.source,
          data: {
            name: values.manufacturerName,
            address: values.address,
            postal_code: values.postalCode,
            city: values.place,
            province: values.province,
            phone_number: values.phoneNumber,
            website: values.website,
            email_address: values.email,
            type: values.type,
          },
        },
      })

      setRefetchingDataAfterSubmit(true)

      const currentBrand = selectedBrand

      await refetchBrands({ category_id: selectedCategoryId })
      await refetchParty({
        brand_id: values.brandId,
        source: values.source,
      })

      if (currentBrand) {
        setSelectedBrand(currentBrand)
      }

      setRefetchingDataAfterSubmit(false)

      toast.success(t('gpsrForm.toast.updated'))
    } catch (err) {
      toast.error(t('gpsrForm.toast.error'))
      setRefetchingDataAfterSubmit(false)
    } finally {
      setSubmitting(false)
    }
  }

  const categoryOptions =
    categories?.gpsrCategoryList?.map((c) => ({
      label: c.category,
      value: c.category,
      category: UNCATEGORIZED_GROUP_KEY,
    })) || []

  const brandOptions =
    brands?.gpsrBrandsByCategory
      ?.map((b) => ({
        label: b.brand,
        value: serializeBrandIdentifier(createBrandIdentifier(b.id, b.source)),
        category:
          Number(b.has_party) === 1
            ? 'gpsrForm.allBrands'
            : 'gpsrForm.brandsWithoutData',
        id: b.id,
        source: b.source,
      }))
      .sort((a, b) => {
        if (a.category === b.category) {
          return 0
        }
        return a.category === 'gpsrForm.brandsWithoutData' ? -1 : 1
      }) || []

  // Store setFieldValue reference for use in effects
  const [formFieldSetter, setFormFieldSetter] = useState(null)

  // Update setFieldValue reference when it changes
  const updateFormFieldSetter = useCallback(
    (setFieldValue) => {
      if (formFieldSetter !== setFieldValue) {
        setFormFieldSetter(() => setFieldValue)
      }
    },
    [formFieldSetter],
  )

  // Helper to get form fields that should be reset when selection changes
  const getFormFieldsToReset = () => [
    'manufacturerName',
    'address',
    'place',
    'province',
    'postalCode',
    'phoneNumber',
    'website',
    'email',
    'type',
  ]

  useEffect(() => {
    if (!formFieldSetter || !selectedBrand?.id) {
      return
    }

    // Always clear fields when brand changes
    getFormFieldsToReset().forEach((field) => {
      formFieldSetter(field, '')
    })
  }, [formFieldSetter, selectedBrand])

  useEffect(() => {
    if (!formFieldSetter || !selectedBrand?.id || partyLoading || partyError) {
      return
    }

    if (partyData?.gpsrPartiesByBrand?.length > 0) {
      const partyInfo = partyData.gpsrPartiesByBrand[0]
      formFieldSetter('manufacturerName', partyInfo.name || '')
      formFieldSetter('address', partyInfo.address || '')
      formFieldSetter('place', partyInfo.city || '')
      formFieldSetter('province', partyInfo.province || '')
      formFieldSetter('postalCode', partyInfo.postal_code || '')
      formFieldSetter('phoneNumber', partyInfo.phone_number || '')
      formFieldSetter('website', partyInfo.website || '')
      formFieldSetter('email', partyInfo.email_address || '')
      formFieldSetter('type', partyInfo.type || '')
    }
  }, [formFieldSetter, selectedBrand, partyLoading, partyData, partyError])

  if (isFormLoading || isError) {
    return <LoadingIndicator error={isError} />
  }

  return (
    <Formik
      validateOnChange
      validateOnBlur
      validateOnMount
      initialValues={getInitialFormValues()}
      enableReinitialize
      validationSchema={GpsrFormSchema(t)}
      onSubmit={handleFormSubmit}
    >
      {({ handleSubmit, values, isValid, setFieldValue, dirty }) => {
        // Update setFieldValue reference for use in effects
        updateFormFieldSetter(setFieldValue)

        // Helper function to clear form fields
        const clearContactFormFields = () => {
          getFormFieldsToReset().forEach((field) => {
            setFieldValue(field, '')
          })
        }

        const handleCategoryChange = (value) => {
          setRefetchingDataAfterSubmit(false)

          if (!value && !categoriesLoading) {
            setFieldValue('category', '')
            setFieldValue('categoryId', null)
            setFieldValue('brand', '')
            setFieldValue('brandId', null)
            setFieldValue('brandLabel', '')
            setFieldValue('source', '')
            setSelectedCategoryId(null)
            setSelectedBrand(null)
            clearContactFormFields()
            return
          }
          const selectedCategory = categories?.gpsrCategoryList?.find(
            (c) => c.category === value,
          )
          setFieldValue('category', value)
          setFieldValue('categoryId', selectedCategory?.id || null)
          setFieldValue('brand', '')
          setFieldValue('brandId', null)
          setFieldValue('brandLabel', '')
          setFieldValue('source', '')
          setSelectedCategoryId(selectedCategory?.id || null)
          setSelectedBrand(null)
          clearContactFormFields()
        }

        const handleBrandChange = (value) => {
          setRefetchingDataAfterSubmit(false)

          if (!value && !brandsLoading) {
            setFieldValue('brand', '')
            setFieldValue('brandId', null)
            setFieldValue('brandLabel', '')
            setFieldValue('source', '')
            setSelectedBrand(null)
            clearContactFormFields()
            return
          }

          const brandIdentifier = deserializeBrandIdentifier(value)

          const brandData = brandIdentifier
            ? brands?.gpsrBrandsByCategory?.find(
                (b) =>
                  b.id === brandIdentifier.id &&
                  b.source === brandIdentifier.source,
              ) || null
            : null

          setFieldValue('brand', value)
          setFieldValue('brandId', brandIdentifier?.id || null)
          setFieldValue('brandLabel', brandData?.brand || '')
          setFieldValue('source', brandIdentifier?.source || '')
          setSelectedBrand(brandIdentifier)
        }

        const handleAddNewBrand = () => {
          setIsAddBrandDialogOpen(true)
        }

        const handleCloseAddBrandDialog = () => {
          setIsAddBrandDialogOpen(false)
          setNewBrandName('')
        }

        const handleSubmitNewBrand = async () => {
          if (!newBrandName.trim()) {
            toast.error(t('gpsrForm.brandNameRequired'))
            return
          }

          if (!selectedCategoryId) {
            toast.error(t('gpsrForm.categoryRequired', 'Category is required'))
            return
          }

          const existingBrand = brands?.gpsrBrandsByCategory?.find(
            (brand) =>
              brand.brand.toLowerCase() === newBrandName.trim().toLowerCase(),
          )
          if (existingBrand) {
            toast.error(
              t(
                'gpsrForm.brandAlreadyExists',
                'Brand already exists in this category',
              ),
            )
            return
          }

          try {
            const result = await insertBrand({
              variables: {
                brand: newBrandName.trim(),
                categoryId: selectedCategoryId,
                svidMerk: null,
              },
            })

            const newBrand = result.data?.insertBrand
            if (newBrand) {
              setIsAddBrandDialogOpen(false)
              setNewBrandName('')

              setRefetchingDataAfterSubmit(true)

              // Refetch the brands list so the dropdown options and select the newly added brand
              await refetchBrands({ category_id: selectedCategoryId })

              const newBrandIdentifier = createBrandIdentifier(
                newBrand.id,
                newBrand.source || GPSR_BRANDS_SOURCES.TENANT,
              )

              setFieldValue(
                'brand',
                serializeBrandIdentifier(newBrandIdentifier),
              )
              setFieldValue('brandId', newBrand.id)
              setFieldValue('brandLabel', newBrand.brand)
              setFieldValue(
                'source',
                newBrand.source || GPSR_BRANDS_SOURCES.TENANT,
              )
              setSelectedBrand(newBrandIdentifier)

              setRefetchingDataAfterSubmit(false)

              toast.success(t('gpsrForm.brandAdded'))
            }
          } catch (error) {
            toast.error(t('gpsrForm.brandAddError', 'Failed to add brand'))
            setRefetchingDataAfterSubmit(false)
          }
        }

        const isSubmitDisabled = !dirty || !isValid || !values.brandId

        return (
          <StyledForm>
            <Typography type="Level1Heading">{t('gpsrDetails')}</Typography>

            <Container>
              <Field
                name="category"
                component={StyledDropdown}
                label={t('gpsrForm.category')}
                required
                items={categoryOptions}
                onChange={handleCategoryChange}
                filled
              />
              <CategorizedDropdown
                name="brand"
                id="brand"
                label={t('gpsrForm.brand')}
                required={!!values.categoryId}
                items={brandOptions}
                value={values.brand}
                onChange={handleBrandChange}
                onActionClick={handleAddNewBrand}
                showActionButton={!!values.categoryId && !brandsLoading}
                actionButtonText={t('gpsrForm.addBrand')}
                filled
                disabled={!values.categoryId || brandsLoading}
              />
            </Container>

            {values.brandId && (
              <>
                {partyLoading ? (
                  <LoadingIndicator />
                ) : partyData?.gpsrPartiesByBrand?.length === 0 ? (
                  <>
                    <Typography type="Level2Heading">
                      {t('contactData')}
                    </Typography>

                    <Typography type="Level3Heading">
                      {t('gpsrForm.noData')}
                    </Typography>

                    <Container>
                      <Field
                        name="type"
                        component={StyledDropdown}
                        label={t('gpsrForm.typeLabel')}
                        required
                        items={TYPE_OPTIONS(t)}
                        filled
                      />
                    </Container>

                    {values.type && (
                      <FormSection>
                        <Container>
                          <Field
                            name="manufacturerName"
                            component={StyledTextInput}
                            label={t('name')}
                            required
                            filled
                          />
                        </Container>

                        <Typography type="Level3Heading">
                          {t('gpsrForm.address')}
                        </Typography>
                        <Container>
                          <Field
                            name="place"
                            component={StyledTextInput}
                            label={t('place')}
                            required
                            filled
                          />
                          <Field
                            name="address"
                            component={StyledTextInput}
                            label={t('gpsrForm.streetName')}
                            required
                            filled
                          />
                          <Field
                            name="province"
                            component={StyledTextInput}
                            label={t('gpsrForm.province')}
                            required
                            filled
                          />
                          <Field
                            name="postalCode"
                            component={StyledTextInput}
                            label={t('postalcode')}
                            required
                            filled
                          />
                        </Container>

                        <Typography type="Level3Heading">
                          {t('gpsrForm.contactDetails')}
                        </Typography>
                        <Container>
                          <Field
                            name="phoneNumber"
                            component={StyledTextInput}
                            label={t('gpsrForm.phoneNumber')}
                            required
                            filled
                          />
                          <Field
                            name="website"
                            component={StyledTextInput}
                            label={t('gpsrForm.website')}
                            filled
                          />
                          <Field
                            name="email"
                            component={StyledTextInput}
                            label={t('email')}
                            required
                            filled
                          />
                        </Container>

                        <SubmitButton
                          type="submit"
                          level="cta"
                          disabled={isSubmitDisabled}
                          isLoading={updatePartyLoading}
                          onClick={handleSubmit}
                        >
                          {t('saveChanges')}
                        </SubmitButton>
                      </FormSection>
                    )}
                  </>
                ) : (
                  <>
                    <Typography type="Level2Heading">
                      {t('contactData')}{' '}
                      {values.type &&
                        t(`gpsrForm.${values.type.toLowerCase()}`)}
                    </Typography>

                    <Container>
                      <Field
                        name="manufacturerName"
                        component={StyledTextInput}
                        label={t('name')}
                        required
                        filled
                      />
                    </Container>

                    <Typography type="Level3Heading">
                      {t('gpsrForm.address')}
                    </Typography>
                    <Container>
                      <Field
                        name="place"
                        component={StyledTextInput}
                        label={t('place')}
                        required
                        filled
                      />
                      <Field
                        name="address"
                        component={StyledTextInput}
                        label={t('gpsrForm.streetName')}
                        required
                        filled
                      />
                      <Field
                        name="province"
                        component={StyledTextInput}
                        label={t('gpsrForm.province')}
                        required
                        filled
                      />
                      <Field
                        name="postalCode"
                        component={StyledTextInput}
                        label={t('postalcode')}
                        required
                        filled
                      />
                    </Container>

                    <Typography type="Level3Heading">
                      {t('gpsrForm.contactDetails')}
                    </Typography>
                    <Container>
                      <Field
                        name="phoneNumber"
                        component={StyledTextInput}
                        label={t('gpsrForm.phoneNumber')}
                        required
                        filled
                      />
                      <Field
                        name="website"
                        component={StyledTextInput}
                        label={t('gpsrForm.website')}
                        filled
                      />
                      <Field
                        name="email"
                        component={StyledTextInput}
                        label={t('email')}
                        required
                        filled
                      />
                    </Container>

                    <SubmitButton
                      type="submit"
                      level="cta"
                      disabled={isSubmitDisabled}
                      isLoading={updatePartyLoading}
                      onClick={handleSubmit}
                    >
                      {t('saveChanges')}
                    </SubmitButton>
                  </>
                )}
              </>
            )}

            <FlexibleDialog
              open={isAddBrandDialogOpen}
              closeHandler={handleCloseAddBrandDialog}
              title={t('gpsrForm.addNewBrandTitle', {
                brand: values.brandLabel || '',
              })}
              content={
                <DialogContent>
                  <Typography type="Body">
                    {t('gpsrForm.addNewBrandDescription')}
                  </Typography>
                  <NewTextInput
                    id="new-brand-name"
                    label={t('gpsrForm.brand')}
                    value={newBrandName || ''}
                    onChange={(value) => setNewBrandName(value)}
                    required
                    filled
                    disabled={insertBrandLoading}
                  />
                </DialogContent>
              }
              cancel
              cancelText={t('cancel')}
              onCancel={handleCloseAddBrandDialog}
              onSubmit={handleSubmitNewBrand}
              submitText={t('gpsrForm.addBrand')}
              disabled={insertBrandLoading}
              isLoading={insertBrandLoading}
            />
          </StyledForm>
        )
      }}
    </Formik>
  )
}

export default withApolloV4Provider(LocationsGpsr)
